import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import type {
  CursorPage,
  CursorPageArgs,
} from "../../../ui/components/Table/hooks/useInfiniteListQuery";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import { fetchAnalyticsErrorsFeed, type AnalyticsErrorEvent } from "../../lib/api/product-analytics";
import { createErrorColumns } from "./productAnalyticsColumns";
import { createUserEntityFilter, createOrganizationEntityFilter } from "./historyEntityFilters";
import { ProductAnalyticsTabs } from "./ProductAnalyticsTabs";
import * as pageStyles from "../users/UsersListPage.css";

export function ProductAnalyticsErrorsListPage() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { t } = useI18n();

  const token =
    accessToken ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const columns = React.useMemo(
    () =>
      token
        ? createErrorColumns(t, token, {
            user: createUserEntityFilter(token),
            organization: createOrganizationEntityFilter(token),
          })
        : [],
    [t, token],
  );

  const fetchPage = React.useCallback(
    async (args: CursorPageArgs): Promise<CursorPage<AnalyticsErrorEvent>> => {
      if (!token) return { data: [], nextCursor: null, hasMore: false };
      return fetchAnalyticsErrorsFeed(token, {
        limit: args.limit,
        cursor: args.cursor,
        order: args.order,
        filters: args.filters,
      });
    },
    [token],
  );

  const cursorFeed = React.useMemo(() => ({ fetchPage }), [fetchPage]);

  if (!token) {
    return (
      <div className={pageStyles.page}>
        <Callout variant="error">{t("pro.system.productAnalytics.signInRequired")}</Callout>
      </div>
    );
  }

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <Text level="h1" color="text" weight="bold">
          {t("pro.system.productAnalytics.title")}
        </Text>
      </div>

      <ProductAnalyticsTabs active="errors" />

      <div className={pageStyles.tableHost}>
        <ListView<AnalyticsErrorEvent>
          listId="product-analytics-errors"
          columns={columns}
          fetchList={async () => ({ data: [], total: 0 })}
          cursorFeed={cursorFeed}
          enabledViews={["table"]}
          defaultSort={{ sort_by: "occurredAt", order: "desc" }}
          defaultPageSize={50}
          searchPlaceholder={t("pro.system.productAnalytics.searchErrors")}
          emptyTitle={t("pro.system.productAnalytics.errorsEmptyTitle")}
          emptySubtitle={t("pro.system.productAnalytics.errorsEmptySubtitle")}
          timeRangeFilter={{ enabled: true }}
          onRowClick={(row) =>
            void navigate({
              to: "/system/product-analytics/errors/$errorId",
              params: { errorId: row.id },
            })
          }
          fillContainer
        />
      </div>
    </div>
  );
}
